import { useState, useCallback } from 'react';
import {
  DataTable,
  Badge,
  Filters,
  ChoiceList,
  Button,
  Modal,
  Scrollable,
  Pagination,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { fetchEventLogs, type EventLog } from '../utils/api';

const PAGE_SIZE = 20;

export default function EventLogPage() {
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventLog | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['eventLogs', page, sourceFilter, typeFilter],
    queryFn: () => fetchEventLogs({
      page,
      limit: PAGE_SIZE,
      source: sourceFilter.length > 0 ? sourceFilter : undefined,
      type: typeFilter.length > 0 ? typeFilter : undefined,
    }),
  });

  const events = data?.events ?? [];
  const totalPages = data?.totalPages ?? 1;

  const handleSourceChange = useCallback((value: string[]) => {
    setSourceFilter(value);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((value: string[]) => {
    setTypeFilter(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSourceFilter([]);
    setTypeFilter([]);
    setPage(1);
  }, []);

  const filters = [
    {
      key: 'source',
      label: 'Source',
      filter: (
        <ChoiceList
          title="Source"
          titleHidden
          choices={[
            { label: 'Shopify', value: 'shopify' },
            { label: 'Jitsu', value: 'jitsu' },
          ]}
          selected={sourceFilter}
          onChange={handleSourceChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'type',
      label: 'Type',
      filter: (
        <ChoiceList
          title="Type"
          titleHidden
          choices={[
            { label: 'Fulfillment Created', value: 'fulfillment_created' },
            { label: 'Tracking Updated', value: 'tracking_updated' },
            { label: 'Shipment Created', value: 'shipment_created' },
            { label: 'Webhook Received', value: 'webhook_received' },
          ]}
          selected={typeFilter}
          onChange={handleTypeChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [
    ...(sourceFilter.length > 0
      ? [{ key: 'source', label: `Source: ${sourceFilter.join(', ')}`, onRemove: () => setSourceFilter([]) }]
      : []),
    ...(typeFilter.length > 0
      ? [{ key: 'type', label: `Type: ${typeFilter.join(', ')}`, onRemove: () => setTypeFilter([]) }]
      : []),
  ];

  const rows = events.map((event) => [
    new Date(event.createdAt).toLocaleString(),
    <Badge tone={event.source === 'shopify' ? 'info' : 'attention'}>{event.source}</Badge>,
    event.type,
    event.key,
    <Badge tone={event.result === 'success' ? 'success' : 'critical'}>{event.result}</Badge>,
    <Button size="slim" onClick={() => setSelectedEvent(event)}>View</Button>,
  ]);

  if (error) {
    return (
      <div className="jitsu-card">
        <p style={{ color: 'var(--jitsu-sienna)' }}>
          Failed to load events: {(error as Error).message}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="jitsu-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px' }}>
          <Filters
            queryValue=""
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={() => {}}
            onQueryClear={() => {}}
            onClearAll={handleClearFilters}
            hideQueryField
          />
        </div>

        {events.length === 0 && !isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <img
              src="/images/jitsu-comet.gif"
              alt="No events"
              style={{ height: '60px', marginBottom: '16px', opacity: 0.6 }}
            />
            <h3 style={{ color: 'var(--jitsu-grey)', marginBottom: '8px' }}>No events found</h3>
            <p style={{ color: 'var(--jitsu-mid-grey)' }}>
              Events will appear here as webhooks are processed.
            </p>
          </div>
        ) : (
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
            headings={['Time', 'Source', 'Type', 'Key', 'Result', 'Actions']}
            rows={rows}
            hoverable
          />
        )}

        {totalPages > 1 && (
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPrevious={() => setPage((p) => p - 1)}
              onNext={() => setPage((p) => p + 1)}
            />
          </div>
        )}
      </div>

      <Modal
        open={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
        title="Event Details"
        size="large"
      >
        <Modal.Section>
          {selectedEvent && <EventDetails event={selectedEvent} />}
        </Modal.Section>
      </Modal>
    </div>
  );
}

function EventDetails({ event }: { event: EventLog }) {
  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ color: 'var(--jitsu-grey)', marginBottom: '12px' }}>Metadata</h4>
        <p><strong>ID:</strong> {event.id}</p>
        <p><strong>Source:</strong> {event.source}</p>
        <p><strong>Type:</strong> {event.type}</p>
        <p><strong>Key:</strong> {event.key}</p>
        <p><strong>Result:</strong> {event.result}</p>
        <p><strong>Created:</strong> {new Date(event.createdAt).toLocaleString()}</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ color: 'var(--jitsu-grey)', marginBottom: '12px' }}>Payload</h4>
        <Scrollable style={{ height: '300px' }}>
          <pre style={{
            background: 'var(--jitsu-tone-grey)',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '12px',
            overflow: 'auto',
          }}>
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </Scrollable>
      </div>

      {event.errorMessage && (
        <div>
          <h4 style={{ color: 'var(--jitsu-sienna)', marginBottom: '12px' }}>Error</h4>
          <p style={{ color: 'var(--jitsu-sienna)' }}>{event.errorMessage}</p>
        </div>
      )}
    </div>
  );
}

